import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, CalendarDays, Sparkles, ArrowUpRight, CheckCircle2, Calendar as CalendarIcon,
  ClipboardList, FileText, Plane, Fingerprint,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function pad(n) { return String(n).padStart(2, "0"); }
function monthFirst(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; }
function isoDate(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }

function GradePill({ grade, isHoliday, isOff }) {
  if (isHoliday) return <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-red-100 brand-text">Hol</span>;
  if (isOff) return <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-slate-100 text-slate-600">Off</span>;
  if (grade === "P") return <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-emerald-100 text-emerald-700">P</span>;
  if (grade === "H") return <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-amber-100 text-amber-700">H</span>;
  if (grade === "A") return <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-red-100 text-red-700">A</span>;
  return null;
}

function popoverDateRange(start, end) {
  if (!end || start === end) {
    const dt = new Date(start);
    return `${dt.toLocaleDateString("en-US", { weekday: "long" })}, ${dt.getDate()} ${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][dt.getMonth()]} ${dt.getFullYear()}`;
  }
  const a = new Date(start), b = new Date(end);
  return `${a.getDate()}–${b.getDate()} ${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][b.getMonth()]} ${b.getFullYear()}`;
}

function HolidayPopoverBody({ holiday }) {
  const h = holiday.holiday || {};
  const isFixed = holiday.kind === "fixed";
  const color = h.color || (isFixed ? "#7c3aed" : "#a16207");
  const headerBg = isFixed ? "bg-purple-50" : "bg-yellow-50";
  const headerText = isFixed ? "text-purple-900" : "text-yellow-900";
  const subText = isFixed ? "text-purple-700" : "text-yellow-700";
  return (
    <div data-testid="holiday-popover-body">
      <div className={`${headerBg} px-4 py-3 border-b border-slate-100 flex items-start gap-3`}>
        <div className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-lg"
             style={{ background: color }}>
          {(h.name || "?")[0]}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-semibold ${headerText} truncate`}>{h.name || "Holiday"}</div>
          <div className={`text-[11px] ${subText} mt-0.5`}>
            {isFixed ? (
              <span className="inline-flex items-center gap-1">
                <CalendarIcon className="h-3 w-3" /> Fixed Holiday · Mandatory
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Your Optional Leave
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="px-4 py-3 space-y-2 text-sm">
        <div className="flex items-center gap-2 text-slate-700">
          <CalendarDays className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <span>{popoverDateRange(h.start_date, h.end_date)}</span>
        </div>
        {h.description && (
          <div className="text-[12px] text-slate-600 italic border-l-2 border-slate-200 pl-3 py-0.5">
            {h.description}
          </div>
        )}
        {!isFixed && holiday.application && (
          <div className="bg-emerald-50 border border-emerald-100 rounded p-2 text-[11px] flex items-start gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-emerald-900">Approved as your optional leave</div>
              {holiday.application.decision_by_name && (
                <div className="text-emerald-700">
                  by {holiday.application.decision_by_name}
                </div>
              )}
              {holiday.application.decision_remarks && (
                <div className="text-emerald-700 italic mt-0.5">
                  &ldquo;{holiday.application.decision_remarks}&rdquo;
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100">
        <Link
          to="/my-portal/holidays"
          className="text-[12px] font-medium brand-text inline-flex items-center gap-1 hover:underline"
          data-testid="holiday-popover-link"
        >
          Go to Holidays <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity Popover Body — shared between delegation/checklist/leave icons
// Displays a clickable list of items for the day. Clicking an item navigates
// the user to the relevant page so they can act on the task.
// ---------------------------------------------------------------------------
function ActivityPopoverBody({ type, items }) {
  const cfg = {
    delegation: {
      icon: FileText, color: "text-violet-700", bg: "bg-violet-50",
      header: "Delegated Tasks", linkBase: "/delegation/tasks",
      itemLink: (it) => it.id ? `/delegation/tasks/${it.id}` : "/delegation/tasks",
    },
    checklist: {
      icon: ClipboardList, color: "text-blue-700", bg: "bg-blue-50",
      header: "Checklist Tasks", linkBase: "/checklist",
      itemLink: () => "/checklist",
    },
    leave: {
      icon: Plane, color: "text-amber-700", bg: "bg-amber-50",
      header: "Leaves", linkBase: "/my-portal/leaves",
      itemLink: () => "/my-portal/leaves",
    },
  }[type] || {};
  const Icon = cfg.icon || FileText;
  const STATUS_COLORS = {
    pending: "bg-slate-100 text-slate-700",
    in_progress: "bg-blue-100 text-blue-700",
    completed: "bg-emerald-100 text-emerald-700",
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-red-100 text-red-700",
    overdue: "bg-red-100 text-red-700",
  };
  const PRIORITY_COLORS = {
    high: "bg-red-100 text-red-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-slate-100 text-slate-600",
  };

  return (
    <div data-testid={`activity-popover-body-${type}`}>
      <div className={`${cfg.bg} px-4 py-2.5 border-b border-slate-100 flex items-center gap-2`}>
        <Icon className={`h-4 w-4 ${cfg.color}`} />
        <div className={`text-sm font-semibold ${cfg.color}`}>
          {cfg.header} ({items?.length || 0})
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
        {(items || []).map((it, idx) => {
          const title = it.title || it.leave_type || "Untitled";
          const status = it.status || (it.overdue ? "overdue" : "");
          const priority = it.priority;
          const taskNum = it.task_number;
          const assigner = it.assigned_by_name;
          const isHalfDay = it.is_half_day;
          return (
            <Link
              key={it.id || idx}
              to={cfg.itemLink ? cfg.itemLink(it) : cfg.linkBase}
              className="block px-4 py-2.5 hover:bg-slate-50 transition-colors"
              data-testid={`activity-item-${type}-${idx}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {taskNum ? <span className="text-slate-400 font-mono mr-1">#{taskNum}</span> : null}
                    {title}
                  </div>
                  {assigner && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      from {assigner}
                    </div>
                  )}
                  {type === "leave" && (
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {it.from_date}{it.to_date && it.to_date !== it.from_date ? ` → ${it.to_date}` : ""}
                      {isHalfDay ? " · Half day" : ""}
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {status && (
                    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}>
                      {status.replace("_", " ")}
                    </span>
                  )}
                  {priority && (
                    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${PRIORITY_COLORS[priority] || "bg-slate-100 text-slate-600"}`}>
                      {priority}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
        {(!items || items.length === 0) && (
          <div className="px-4 py-6 text-center text-sm text-slate-400">
            No items
          </div>
        )}
      </div>
      <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-right">
        <Link
          to={cfg.linkBase}
          className={`text-[11px] font-semibold inline-flex items-center gap-1 ${cfg.color} hover:underline`}
          data-testid={`activity-popover-cta-${type}`}
        >
          Open {cfg.header} <ArrowUpRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}



// Expand multi-day holiday into individual ISO dates
function expandHolidayDates(startISO, endISO) {
  const out = [];
  if (!startISO) return out;
  const start = new Date(startISO);
  const end = endISO ? new Date(endISO) : start;
  const cur = new Date(start);
  while (cur <= end) {
    out.push(isoDate(cur.getFullYear(), cur.getMonth(), cur.getDate()));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export default function PortalAttendance() {
  const today = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [holidaysByDate, setHolidaysByDate] = useState({});
  const [overlayByDate, setOverlayByDate] = useState({});

  const monthStr = monthFirst(cursor);

  // Fetch attendance for current month
  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/my-portal/attendance", { params: { month: monthStr } });
        setRows(data.rows || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      } finally {
        setLoading(false);
      }
    })();
  }, [monthStr]);

  // Phase 5G-E — Fetch live overlay (biometric, delegations, checklist, leaves)
  useEffect(() => {
    (async () => {
      try {
        const m = monthStr.slice(0, 7);
        const { data } = await api.get("/my-portal/calendar-overlay", { params: { month: m } });
        setOverlayByDate(data.days || {});
      } catch (e) {
        // overlay is best-effort — calendar still works without it
        console.warn("[PortalAttendance] overlay load failed", e);
        setOverlayByDate({});
      }
    })();
  }, [monthStr]);

  // Fetch holidays once (full list across years); refresh if needed
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/my-portal/holidays");
        const map = {};
        for (const h of data?.holidays || []) {
          const dates = expandHolidayDates(h.start_date, h.end_date);
          if (h.holiday_type === "fixed") {
            for (const iso of dates) {
              // fixed wins over an existing pending/rejected optional record
              map[iso] = { kind: "fixed", name: h.name, color: h.color, holiday: h };
            }
          } else {
            // optional: only mark as "availed" if employee's application is approved
            const status = h.my_application?.status;
            if (status === "approved") {
              for (const iso of dates) {
                if (!map[iso] || map[iso].kind !== "fixed") {
                  map[iso] = {
                    kind: "optional_availed", name: h.name, color: h.color,
                    holiday: h, application: h.my_application,
                  };
                }
              }
            }
          }
        }
        setHolidaysByDate(map);
      } catch (e) {
        // non-fatal — calendar still works, just without holiday overlay
        // eslint-disable-next-line no-console
        console.warn("Holiday overlay load failed", e);
      }
    })();
  }, []);

  const rowsByDay = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const d = r.day ?? (r.date ? new Date(r.date).getDate() : null);
      if (d) m[d] = r;
    }
    return m;
  }, [rows]);

  // Build calendar grid (Mon-first, ISO weekday)
  const year = cursor.getFullYear();
  const monthIdx = cursor.getMonth();
  const firstDate = new Date(year, monthIdx, 1);
  const lastDate = new Date(year, monthIdx + 1, 0);
  const daysInMonth = lastDate.getDate();
  const firstWeekdayMon = (firstDate.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < firstWeekdayMon; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  // Counters
  const counts = rows.reduce((acc, r) => {
    if (r.is_holiday) acc.hol++;
    else if (r.is_off) acc.off++;
    else if (r.grade === "P") acc.p++;
    else if (r.grade === "H") acc.h++;
    else if (r.grade === "A") acc.a++;
    return acc;
  }, { p: 0, h: 0, a: 0, hol: 0, off: 0 });

  const prev = () => setCursor(new Date(year, monthIdx - 1, 1));
  const next = () => setCursor(new Date(year, monthIdx + 1, 1));

  return (
    <div className="space-y-6" data-testid="portal-attendance">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Attendance</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            My Calendar
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={prev}
            className="p-2 rounded-md border border-slate-200 hover:bg-slate-50"
            data-testid="att-prev-month"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="text-sm font-semibold text-slate-900 min-w-[160px] text-center">
            {MONTH_NAMES[monthIdx]} {year}
          </div>
          <button
            onClick={next}
            className="p-2 rounded-md border border-slate-200 hover:bg-slate-50"
            data-testid="att-next-month"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Counts strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="rounded-md bg-emerald-50 border border-emerald-100 px-3 py-2 text-center">
          <div className="text-xl font-bold text-emerald-700">{counts.p}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Present</div>
        </div>
        <div className="rounded-md bg-amber-50 border border-amber-100 px-3 py-2 text-center">
          <div className="text-xl font-bold text-amber-700">{counts.h}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Half-day</div>
        </div>
        <div className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-center">
          <div className="text-xl font-bold text-red-700">{counts.a}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Absent</div>
        </div>
        <div className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-center">
          <div className="text-xl font-bold brand-text">{counts.hol}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Holiday</div>
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2 text-center">
          <div className="text-xl font-bold text-slate-700">{counts.off}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Off</div>
        </div>
      </div>

      {/* Calendar */}
      <section className="bg-white rounded-xl border border-red-100 p-4">
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-widest text-slate-500 mb-2">
          {WEEKDAYS.map((w) => <div key={w}>{w}</div>)}
        </div>
        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">Loading...</div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (d === null) return <div key={i} className="aspect-square" />;
              const row = rowsByDay[d];
              const iso = isoDate(year, monthIdx, d);
              const holiday = holidaysByDate[iso];
              const overlay = overlayByDate[iso];
              const isToday = today.getFullYear() === year && today.getMonth() === monthIdx && today.getDate() === d;

              // Holiday-driven cell styling
              let cellTone = "bg-white";
              let cellBorder = isToday ? "border-red-300 ring-1 ring-red-200" : "border-slate-100";
              if (holiday?.kind === "fixed") {
                cellTone = "bg-purple-50";
                cellBorder = isToday ? "border-purple-400 ring-1 ring-purple-200" : "border-purple-200";
              } else if (holiday?.kind === "optional_availed") {
                cellTone = "bg-yellow-50";
                cellBorder = isToday ? "border-yellow-400 ring-1 ring-yellow-300" : "border-yellow-200";
              }

              const cellInner = (
                <div
                  className={`aspect-square rounded-md border p-1.5 flex flex-col text-left ${cellBorder} ${cellTone} ${holiday ? "cursor-pointer hover:shadow-sm transition-shadow" : ""}`}
                  data-testid={`day-${d}`}
                  title={holiday ? `${holiday.name}${holiday.kind === "optional_availed" ? " (Your optional leave) — click for details" : " — click for details"}` : undefined}
                >
                  <div className={`text-xs font-semibold ${holiday?.kind === "fixed" ? "text-purple-900" : holiday?.kind === "optional_availed" ? "text-yellow-900" : "text-slate-900"}`}>
                    {d}
                  </div>
                  {holiday && (
                    <div className={`mt-0.5 text-[9px] font-semibold leading-tight truncate ${holiday.kind === "fixed" ? "text-purple-700" : "text-yellow-700"}`}
                         data-testid={`holiday-label-${d}`}>
                      {holiday.kind === "optional_availed" && <Sparkles className="inline h-2.5 w-2.5 mr-0.5 -mt-0.5" />}
                      {holiday.name}
                    </div>
                  )}
                  {/* Phase 5G-E overlay — live biometric IN/OUT + counts */}
                  {overlay?.bio && (
                    <div className="mt-0.5 text-[9px] leading-tight font-mono flex items-center gap-0.5"
                         data-testid={`bio-overlay-${d}`}>
                      <Fingerprint className="h-2.5 w-2.5 text-emerald-700 flex-shrink-0" />
                      <span className="text-emerald-800 font-semibold">{overlay.bio.first_in}</span>
                      {!overlay.bio.same_in_out && overlay.bio.last_out && (
                        <>
                          <span className="text-slate-300">·</span>
                          <span className="text-slate-700">{overlay.bio.last_out}</span>
                        </>
                      )}
                    </div>
                  )}
                  {(overlay?.delegations?.length > 0 || overlay?.checklist?.length > 0 || overlay?.leaves?.length > 0) && (
                    <div className="mt-0.5 flex items-center gap-1 text-[10px]" data-testid={`activity-icons-${d}`}>
                      {overlay.leaves?.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center gap-0.5 text-amber-700 hover:bg-amber-50 active:bg-amber-100 rounded px-1 py-0.5 -mx-1 cursor-pointer transition-colors"
                              onClick={(e) => e.stopPropagation()}
                              title={`${overlay.leaves.length} leave${overlay.leaves.length > 1 ? "s" : ""} — click for details`}
                              data-testid={`leave-icon-${d}`}>
                              <Plane className="h-3 w-3" />{overlay.leaves.length}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" sideOffset={6} className="w-72 p-0 overflow-hidden"
                                              data-testid={`leave-popover-${d}`} onClick={(e) => e.stopPropagation()}>
                            <ActivityPopoverBody type="leave" items={overlay.leaves} />
                          </PopoverContent>
                        </Popover>
                      )}
                      {overlay.checklist?.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center gap-0.5 text-blue-700 hover:bg-blue-50 active:bg-blue-100 rounded px-1 py-0.5 -mx-1 cursor-pointer transition-colors"
                              onClick={(e) => e.stopPropagation()}
                              title={`${overlay.checklist.length} checklist task${overlay.checklist.length > 1 ? "s" : ""} — click for details`}
                              data-testid={`checklist-icon-${d}`}>
                              <ClipboardList className="h-3 w-3" />{overlay.checklist.length}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" sideOffset={6} className="w-72 p-0 overflow-hidden"
                                              data-testid={`checklist-popover-${d}`} onClick={(e) => e.stopPropagation()}>
                            <ActivityPopoverBody type="checklist" items={overlay.checklist} />
                          </PopoverContent>
                        </Popover>
                      )}
                      {overlay.delegations?.length > 0 && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex items-center gap-0.5 text-violet-700 hover:bg-violet-50 active:bg-violet-100 rounded px-1 py-0.5 -mx-1 cursor-pointer transition-colors"
                              onClick={(e) => e.stopPropagation()}
                              title={`${overlay.delegations.length} delegated task${overlay.delegations.length > 1 ? "s" : ""} — click for details`}
                              data-testid={`delegation-icon-${d}`}>
                              <FileText className="h-3 w-3" />{overlay.delegations.length}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" sideOffset={6} className="w-80 p-0 overflow-hidden"
                                              data-testid={`delegation-popover-${d}`} onClick={(e) => e.stopPropagation()}>
                            <ActivityPopoverBody type="delegation" items={overlay.delegations} />
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  )}
                  {row && (
                    <div className="mt-auto flex flex-wrap items-center gap-1">
                      <GradePill grade={row.grade} isHoliday={row.is_holiday} isOff={row.is_off} />
                      {row.ot_minutes && row.ot_minutes !== 0 ? (
                        <span className={`text-[10px] font-medium ${row.ot_minutes > 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {row.ot_minutes > 0 ? "+" : ""}{Math.round(row.ot_minutes / 6) / 10}h
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              );

              if (!holiday) {
                return <div key={i}>{cellInner}</div>;
              }

              return (
                <Popover key={i}>
                  <PopoverTrigger asChild data-testid={`holiday-cell-trigger-${d}`}>
                    {cellInner}
                  </PopoverTrigger>
                  <PopoverContent
                    align="start" sideOffset={8} className="w-80 p-0 overflow-hidden"
                    data-testid={`holiday-popover-${d}`}
                  >
                    <HolidayPopoverBody holiday={holiday} />
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-sm text-slate-500 py-3 text-center">
            <CalendarDays className="h-4 w-4 inline-block mr-2 text-slate-400" />
            No attendance recorded for this month yet.
          </div>
        )}

        {/* Legend */}
        <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-4 text-[11px] text-slate-600" data-testid="calendar-legend">
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-purple-300 bg-purple-100"></span>
            Fixed Holiday
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-yellow-300 bg-yellow-100"></span>
            <Sparkles className="h-3 w-3 text-yellow-600" /> Your Optional Leave
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 rounded border border-red-300 bg-red-50"></span>
            Today
          </div>
        </div>
      </section>
    </div>
  );
}
